/**
 * The today's-deliveries card above the delivery-job list (#2588).
 *
 * It reads the dispatch preview of `date` (the same query the dispatch page
 * uses). The big number is completed / (planned + dispatched + in progress +
 * completed): the four stops of the route. Exception and cancelled are not
 * counted, because a cancelled job is never delivered and the ratio could
 * never reach 100%. A missing count (loading or error) shows `—`.
 *
 * This card carries the one hard shadow of the screen (DESIGN.md Part II §4).
 * It must not import `../jobResource`: that module mounts this card.
 */
import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import { JOB_ROUTE_STOPS } from '@lib/job-status-tone';
import { useDispatchPreview } from '../hooks/useDispatchPreview';
import { formatBatchDay } from '../board/formatBatchDay';

const STATION_LABEL = {
  planned: 'job.enum.planned',
  dispatched: 'job.enum.dispatched',
  in_progress: 'job.enum.in_progress',
  completed: 'job.enum.completed',
} as const satisfies Record<(typeof JOB_ROUTE_STOPS)[number], string>;

const MISSING = '—';

export function TodayDeliveryCard({ date }: { date: string }) {
  const { t, i18n } = useTranslation();
  const preview = useDispatchPreview(date);
  // A failed refetch keeps the old data; an error shows dashes, never stale counts.
  const counts = preview.isError ? undefined : preview.data?.statusCounts;
  const total = counts ? JOB_ROUTE_STOPS.reduce((n, stop) => n + counts[stop], 0) : undefined;
  const completed = counts?.completed;

  return (
    <section className="mr-1.5 flex flex-col border-2 border-emphasis bg-card shadow-hard">
      <h2 className="bg-emphasis px-4 py-2 font-display text-base font-bold text-emphasis-foreground">
        {t('job.today.title', { day: formatBatchDay(date, i18n.language) })}
      </h2>
      <div className="flex flex-col gap-4 px-[18px] py-3.5 sm:flex-row sm:items-center sm:gap-12">
        <div className="flex flex-none items-baseline gap-2.5">
          <span className="font-display text-[40px] font-bold leading-none tracking-[-0.04em] tabular-nums text-primary">
            {completed ?? MISSING}/{total ?? MISSING}
          </span>
          <span className="text-sm text-muted-foreground">
            {preview.isError ? t('job.today.unavailable') : t('dispatch.board.stage.delivered')}
          </span>
        </div>
        <div className="relative min-w-0 flex-1">
          <span
            aria-hidden="true"
            className="absolute left-[12.5%] right-[12.5%] top-[6.5px] h-[3px] bg-route"
          />
          <ol
            aria-label={t('job.today.progressLabel')}
            className="relative m-0 grid list-none grid-cols-4 p-0"
          >
            {JOB_ROUTE_STOPS.map((stop, i) => (
              <li key={stop} className="flex min-w-0 flex-col items-center gap-2">
                <span
                  aria-hidden="true"
                  className={cn(
                    'size-4 flex-none rounded-full border-[3px] border-primary',
                    i === 0 || i === JOB_ROUTE_STOPS.length - 1 ? 'bg-primary' : 'bg-card',
                  )}
                />
                {/* Below `sm` the count drops under its label; from `sm` up it reads "Label count". */}
                <span className="whitespace-nowrap text-center text-[13px] text-foreground">
                  {t(STATION_LABEL[stop])}{' '}
                  <b className="block font-semibold tabular-nums sm:inline">
                    {counts?.[stop] ?? MISSING}
                  </b>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
